import { useContext } from 'react';

import { type QueryParams, getQueryKey, loadActionItems } from 'common/actions/actionItemQueries';
import { isIdeaActionItem, isInsightActionItem } from 'common/constants/autopilotActions';
import { LocationContext } from 'common/containers/RouterContainer';
import asyncConnect from 'common/core/asyncConnect';
import cloneElementWithProps from 'common/core/cloneElementWithProps';
import { isNotNil } from 'common/util/isNil';
import mapify from 'common/util/mapify';

import type { Board } from 'common/api/endpoints/boards';
import type { State } from 'common/reducers/actionItemQueries';

export type EncodedQuery = {
  source?: string;
};

export type LocationWithQuery = {
  pathname: string;
  query: EncodedQuery;
};

type Props = {
  boards: Board[];
  children: React.ReactNode;
  actionItemQueries: State;
};

/**
 * Helper function to merge and sum counts from multiple sources
 */
const mergeSourceCounts = (...sources: Partial<Record<string, number>>[]) => {
  const result: Record<string, number> = {};

  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      if (value !== undefined) {
        result[key] = (result[key] || 0) + value;
      }
    }
  }

  return result;
};

export const getQueryParams = (location: LocationWithQuery): QueryParams => {
  const {
    query: { source },
  } = location;

  return {
    ...(source && { source }),
  };
};

export const getEncodedQueryParams = (queryParams: QueryParams): EncodedQuery => {
  return {
    ...(queryParams.source && { source: queryParams.source }),
  };
};

const mapAndFilterItems = (itemIDs: string[], boards: Board[], actionItemQueries: State) => {
  const boardMap = mapify(boards, '_id');

  return itemIDs
    .map((itemID) => {
      const item = actionItemQueries.items[itemID];

      if (!item) {
        return null;
      } else if (isInsightActionItem(item)) {
        return item;
      } else if (isIdeaActionItem(item)) {
        return item;
      }

      return {
        ...item,
        board: boardMap[item.boardID],
        ...(item.duplicatePost && {
          duplicatePost: {
            ...item.duplicatePost,
            board: boardMap[item.duplicatePost.boardID],
          },
        }),
      };
    })
    .filter(isNotNil);
};

const AutopilotActionItemListContainer = (props: Props) => {
  const { actionItemQueries, boards } = props;

  const location = useContext(LocationContext);

  const getActionItemList = () => {
    const queryParams = getQueryParams(location);
    const query = actionItemQueries.queries[getQueryKey(queryParams)];

    if (!query?.itemIDs || query.loading) {
      return Object.assign({}, query, { loading: true, items: [] });
    }

    return {
      ...query,
      items: mapAndFilterItems(query.itemIDs, boards, actionItemQueries),
    };
  };

  const getActionItemCounts = () => {
    return {
      totalCount: actionItemQueries.count,
      cannyCount: actionItemQueries.postActionsCount,
      totalSourceCount:
        actionItemQueries.draftActionsCount +
        actionItemQueries.ideaActionsCount +
        actionItemQueries.insightActionsCount,
      perSourceCounts: mergeSourceCounts(
        actionItemQueries.draftActionsCountPerSource,
        actionItemQueries.ideaActionsCountPerSource,
        actionItemQueries.insightActionsCountPerSource,
      ),
    };
  };

  return cloneElementWithProps(props.children, {
    ...props,
    actionItemList: getActionItemList(),
    actionItemCounts: getActionItemCounts(),
  });
};

export default asyncConnect(
  [
    {
      promise: ({ store: { dispatch }, location }) => {
        const isFeedbackDiscovery = location.pathname.includes('/admin/autopilot');
        if (!isFeedbackDiscovery) {
          return;
        }

        const queryParams = getQueryParams(location);
        if (queryParams?.source === 'spam') {
          return;
        }

        return dispatch(loadActionItems(queryParams));
      },
    },
  ],
  (state) => ({
    actionItemQueries: state.actionItemQueries,
  }),
)(AutopilotActionItemListContainer);
